import { useCallback } from 'react'
import store from 'store'
import { verifyAuth } from 'store/authSlice'
import { SnackbarType, showSnackbar } from 'utils/snackbar'
import { handleEmailAdd } from 'utils/email-connect'
import { useAppDispatch } from 'hooks'

const READ_SCOPE_MODAL = {
    title: 'Enable AI Email Assistant',
    subtitle: 'Allow Numeo AI to access your inbox and reply to brokers',
}

/**
 * Guards a server-side send (AI Hub / Load Hub) against send-only mailboxes.
 *
 * Onboarding "send-only" users granted write access but never the read scope Numeo needs to
 * monitor the inbox and surface broker replies. When such a mailbox tries to send, we open the
 * provider picker with the "Enable AI email assistant" copy so the user upgrades the grant; on
 * success we refresh auth (so the freshly-stored `['read','send']` scopes propagate) and resume.
 *
 * Gating is deliberately narrow: it fires ONLY when an explicit send-only scope is persisted.
 * Mailboxes with no `scopes` (legacy / existing users) or with `read` present pass through, so
 * nothing in the current TMS or admin onboarding flows breaks.
 */
export const useReadScopeGuard = () => {
    const dispatch = useAppDispatch()

    return useCallback(
        (email: string | undefined, resume: () => void): boolean => {
            if (!email) return true

            const emails = store.getState().auth.application?.integrations?.nylas?.emails
            const entry = emails?.find((e) => e.email?.toLowerCase() === email.toLowerCase())
            const scopes = entry?.scopes
            const needsRead = !!scopes && scopes.length > 0 && !scopes.includes('read')
            if (!needsRead) return true

            handleEmailAdd(
                async () => {
                    await dispatch(verifyAuth())
                    resume()
                },
                (error) => showSnackbar(SnackbarType.ERROR, error),
                (message, type) => showSnackbar(type === 'success' ? SnackbarType.SUCCESS : SnackbarType.ERROR, message),
                // Pin to the exact mailbox being upgraded so the signed-in user skips the account chooser
                // and lands on the scope-consent page — and can't accidentally pick a different account.
                { ...READ_SCOPE_MODAL, loginHint: email }
            )
            return false
        },
        [dispatch]
    )
}
